/**
 * Schritt 2: Simulations-Parameter
 * Zentrale Konfiguration für alle Simulationstypen
 */

import React, { useState } from 'react';
import { Alert, Box, Button, Container, Header, SpaceBetween } from '@cloudscape-design/components';

import { AppZustand, SimulationOptions, SteuerParameter } from '@/types';
import { CrisisDefinitionType, ThreeTopfYearData } from '@/types/simulation';
import { SimulationControls } from '../components/simulation/SimulationControls';
import { RateCalculationService } from '../services/RateCalculationService';
import { SharedRateGenerator } from '../services/SharedRateGenerator';
import { garchSequenceCache } from '../services/CacheManager';
import { ThreeTopfSimulationEngine } from '../services/ThreeTopfSimulationEngine';
import { GARCHComparisonTab } from '../components/simulation/GARCHComparisonTab';
import { GARCHValidationPanel } from '../components/simulation/GARCHValidationPanel';
import { GARCHEducationPanel } from '../components/simulation/GARCHEducationPanel';
import { GARCHUtils } from '../utils/garchModeling';

/**
 * Leitet aus der aktuell gewählten Renditeoption die Nicht-GARCH-Variante
 * für den Standard-Vergleichslauf ab.
 */
const toStandardInvestmentReturnOption = (option: string): string => {
  switch (option) {
    case 'historische-garch':
      return 'historische-rendite';
    case 'statistisch-garch':
      return 'statistisch-rendite';
    default:
      return option;
  }
};

/**
 * Leitet aus der aktuell gewählten Renditeoption die GARCH-Variante
 * für den GARCH-Vergleichslauf ab.
 */
const toGARCHInvestmentReturnOption = (option: string): string => {
  if (GARCHUtils.isGARCHOption(option)) {
    return option;
  }
  return GARCHUtils.convertToGARCHOption(option) || 'historische-garch';
};

interface Schritt2Props {
  steuerParameter: SteuerParameter | null;
  simulationOptions: SimulationOptions;
  crisisDefinition: string;
  onSimulationOptionsUpdate: (options: Partial<SimulationOptions>) => void;
  onCrisisDefinitionUpdate: (crisisDefinition: string) => void;
  simulationState: AppZustand['simulationState'];
  onSimulationStateUpdate: (updates: Partial<AppZustand['simulationState']>) => void;
}

/**
 * Komponente für die zentrale Simulations-Parameter-Konfiguration
 */
const Schritt2: React.FC<Schritt2Props> = ({
  steuerParameter,
  simulationOptions,
  crisisDefinition,
  onSimulationOptionsUpdate,
  onCrisisDefinitionUpdate,
  simulationState,
  onSimulationStateUpdate,
}) => {
  // Extract options from centralized state
  const { inflationOption, investmentReturnOption, tagesgeldOption, zinstopfOption } =
    simulationOptions;

  // GARCH visibility state
  const [showGARCHFeatures, setShowGARCHFeatures] = useState(false);

  // Lokale Vergleichsergebnisse, damit Standard- und GARCH-Lauf
  // nebeneinander bestehen bleiben und nicht gegenseitig geleert werden
  const [standardComparisonData, setStandardComparisonData] = useState<ThreeTopfYearData[] | null>(
    null
  );
  const [garchComparisonData, setGarchComparisonData] = useState<ThreeTopfYearData[] | null>(null);
  const [runningComparison, setRunningComparison] = useState<'standard' | 'garch' | null>(null);

  // Create handlers that update centralized state
  const setCrisisDefinition = (value: string) => onCrisisDefinitionUpdate(value);
  const setInflationOption = (value: string) =>
    onSimulationOptionsUpdate({ inflationOption: value });
  const setInvestmentReturnOption = (value: string) =>
    onSimulationOptionsUpdate({ investmentReturnOption: value });
  const setTagesgeldOption = (value: string) =>
    onSimulationOptionsUpdate({ tagesgeldOption: value });
  const setZinstopfOption = (value: string) => onSimulationOptionsUpdate({ zinstopfOption: value });

  // Extract simulation state from global state
  const { simulationData, oneTopfSimulationData } = simulationState;

  // Create simulation runners
  const runComparisonSimulation = async (mode: 'standard' | 'garch') => {
    if (!steuerParameter) {return;}

    // Standard-Lauf rechnet explizit mit einer Nicht-GARCH-Option,
    // GARCH-Lauf explizit mit einer GARCH-Option
    const effectiveInvestmentReturnOption =
      mode === 'garch'
        ? toGARCHInvestmentReturnOption(investmentReturnOption)
        : toStandardInvestmentReturnOption(investmentReturnOption);

    setRunningComparison(mode);
    onSimulationStateUpdate({
      isSimulating: true,
      simulationError: null,
    });

    try {
      // Raten-Caches vor jedem Lauf leeren (gleiches Muster wie Schritt 3),
      // damit Wiederholungsläufe frische Zufallsraten erhalten
      RateCalculationService.clearMasterSeeds();
      SharedRateGenerator.clearStaticCache();
      garchSequenceCache.clear();

      const engine = new ThreeTopfSimulationEngine(steuerParameter, {
        inflationOption,
        investmentReturnOption: effectiveInvestmentReturnOption,
        tagesgeldOption,
        zinstopfOption,
      }, crisisDefinition as CrisisDefinitionType);
      const data = await engine.runSimulation();

      if (mode === 'garch') {
        setGarchComparisonData(data);
      } else {
        setStandardComparisonData(data);
      }

      onSimulationStateUpdate({
        isSimulating: false,
      });
    } catch (error) {
      onSimulationStateUpdate({
        isSimulating: false,
        simulationError: error as any,
      });
    } finally {
      setRunningComparison(null);
    }
  };

  // Check if current simulation options use GARCH
  const currentSimulationOptions = {
    inflationOption,
    investmentReturnOption,
    tagesgeldOption,
    zinstopfOption,
  };
  const oneTopfSimulationOptions = {
    inflationOption,
    investmentReturnOption,
  };

  const isUsingGARCH = investmentReturnOption && GARCHUtils.isGARCHOption(investmentReturnOption);
  const isUsingGARCH1Topf =
    investmentReturnOption && GARCHUtils.isGARCHOption(investmentReturnOption);

  // Show validation error if parameters are missing
  if (!steuerParameter) {
    return (
      <Container
        header={
          <Header
            variant="h2"
            description="Konfigurieren Sie die Simulationsparameter für alle Simulationstypen."
          >
            Simulations-Parameter
          </Header>
        }
      >
        <Alert type="warning" header="Keine Parameter verfügbar">
          Bitte geben Sie zunächst die Berechnungsparameter in "Schritt 1: Individuelle-Parameter" ein, um
          Simulationsparameter konfigurieren zu können.
        </Alert>
      </Container>
    );
  }

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Konfigurieren Sie die zentralen Simulationsparameter, die für alle Simulationstypen verwendet werden."
        >
          Simulations-Parameter
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        {/* Central Simulation Controls */}
        {steuerParameter ? (
          <SimulationControls
            steuerParameter={steuerParameter}
            crisisDefinition={crisisDefinition}
            inflationOption={inflationOption}
            investmentReturnOption={investmentReturnOption}
            tagesgeldOption={tagesgeldOption}
            zinstopfOption={zinstopfOption}
            onCrisisDefinitionChange={setCrisisDefinition}
            onInflationOptionChange={setInflationOption}
            onInvestmentReturnOptionChange={setInvestmentReturnOption}
            onTagesgeldOptionChange={setTagesgeldOption}
            onZinstopfOptionChange={setZinstopfOption}
          />
        ) : (
          <Alert type="warning">
            Bitte konfigurieren Sie zuerst die Grundparameter in Schritt 1, bevor Sie die Simulationsparameter festlegen können.
          </Alert>
        )}

        {/* GARCH Features Toggle Button */}
        <Box textAlign="center">
          <Button
            variant="normal"
            onClick={() => setShowGARCHFeatures(!showGARCHFeatures)}
            iconName={showGARCHFeatures ? 'angle-up' : 'angle-down'}
            iconAlign="right"
          >
            {showGARCHFeatures
              ? 'GARCH-Features Erklärungen ausblenden'
              : 'GARCH-Features Erklärungen anzeigen'}
          </Button>
        </Box>

        {/* GARCH-Features Container - Only visible when toggled */}
        {showGARCHFeatures && (
          <Container
            header={
              <Header
                variant="h2"
                description="Nutzen Sie erweiterte GARCH-Volatilitätsmodelle für realistische Risikosimulationen und Vergleichsanalysen."
              >
                GARCH-Features
              </Header>
            }
          >
            <SpaceBetween direction="vertical" size="l">
              {/* GARCH Education Panel */}
              <GARCHEducationPanel showAdvancedContent={true} />

              {/* GARCH Validation Panel */}
              <GARCHValidationPanel />

              {/* GARCH Comparison - Only show if we have simulation data */}
              {(simulationData.length > 0 || oneTopfSimulationData.length > 0) && (
                <GARCHComparisonTab
                  standardSimulationData={
                    standardComparisonData ?? (isUsingGARCH ? [] : simulationData)
                  }
                  garchSimulationData={garchComparisonData ?? (isUsingGARCH ? simulationData : [])}
                  /* NOTE: standardOneTopfData/garchOneTopfData only ever hold the single
                     static 1-Topf result from Schritt 3 (whichever mode it happened to be
                     run with), placed into whichever column matches the CURRENT
                     investmentReturnOption - never a freshly (re-)computed 1-Topf run for
                     the clicked comparison mode. This currently has no visible effect:
                     GARCHComparisonTab hardcodes selectedStrategy = 'threeTopf' and never
                     renders the 1-Topf data, so no user-facing "stale data" is shown. If a
                     1-Topf toggle is ever added to GARCHComparisonTab, this wiring will need
                     to run OneTopfSimulationEngine per mode (mirroring runComparisonSimulation
                     for the 3-Topf engine above) instead of relabeling static Schritt 3 data. */
                  standardOneTopfData={isUsingGARCH1Topf ? [] : oneTopfSimulationData}
                  garchOneTopfData={isUsingGARCH1Topf ? oneTopfSimulationData : []}
                  steuerParameter={steuerParameter}
                  standardOptions={{
                    ...currentSimulationOptions,
                    investmentReturnOption: toStandardInvestmentReturnOption(
                      currentSimulationOptions.investmentReturnOption
                    ),
                  }}
                  garchOptions={{
                    ...currentSimulationOptions,
                    investmentReturnOption: toGARCHInvestmentReturnOption(
                      currentSimulationOptions.investmentReturnOption
                    ),
                  }}
                  standardOneTopfOptions={{
                    ...oneTopfSimulationOptions,
                    investmentReturnOption: toStandardInvestmentReturnOption(
                      oneTopfSimulationOptions.investmentReturnOption
                    ),
                  }}
                  garchOneTopfOptions={{
                    ...oneTopfSimulationOptions,
                    investmentReturnOption: toGARCHInvestmentReturnOption(
                      oneTopfSimulationOptions.investmentReturnOption
                    ),
                  }}
                  onRunStandardSimulation={() => {
                    // Standard-Vergleichslauf mit expliziter Nicht-GARCH-Option
                    runComparisonSimulation('standard');
                  }}
                  onRunGARCHSimulation={() => {
                    // GARCH-Vergleichslauf mit expliziter GARCH-Option
                    runComparisonSimulation('garch');
                  }}
                  isRunningStandard={runningComparison === 'standard'}
                  isRunningGARCH={runningComparison === 'garch'}
                />
              )}

              {/* Help message when no simulation data */}
              {simulationData.length === 0 && oneTopfSimulationData.length === 0 && (
                <Alert type="info" statusIconAriaLabel="Info">
                  <strong>GARCH Vergleich:</strong> Führen Sie zunächst Simulationen in "Schritt 3:
                  Einzel-Simulation" durch, um die GARCH-Vergleichsfunktionen zu nutzen. Ändern Sie
                  die Simulationsparameter in dieser Sektion oder in "Schritt 3: Einzel-Simulation",
                  um verschiedene Volatilitäts-Modelle zu vergleichen.
                </Alert>
              )}
            </SpaceBetween>
          </Container>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default Schritt2;
